import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import TablePaginationBar from "../components/TablePaginationBar";
import formatDate from "../utils/formatDate";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import { BsLock } from "react-icons/bs";
import { BsThreeDots } from "react-icons/bs";
import { checkPermissionByKey } from "../utils/checkPermissionByKey";
import { useSelector } from "react-redux";

const BusinessDetails = () => {
  const user = useSelector((state) => state?.user);

  const location = useLocation();
  const { id } = useParams();
  const { business } = location.state || {};
  const [businessData, setBusinessData] = useState(business);

  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  // const [status, setStatus] = useState(business.status);
  const accessCodes = businessData?.access_codes || [];
  const [showModal, setShowModal] = useState(false);
  const [editForm, setEditForm] = useState({
    businessName: "",
    firstName: "",
    lastName: "",
    solutionsProAccess: false,
    sessionsAvailable: "",
  });

  const role = user.user?.role;

  // const { id } = useParams();
  const navigate = useNavigate();

  const [isDeactivated, setIsDeactivated] = useState(businessData?.status);
  const [currentPage, setCurrentPage] = useState(1);
  const codesPerPage = 10;

  // Sort accessCodes with the specified order: Active > Unused > Inactive
  const sortedAccessCodes = accessCodes.sort((a, b) => {
    const order = { Active: 1, Unused: 2, Inactive: 3 };
    return order[a.status] - order[b.status];
  });

  // Define indices for slicing based on pagination
  const indexOfLastCode = currentPage * codesPerPage;
  const indexOfFirstCode = indexOfLastCode - codesPerPage;

  // Slice the sorted array to get only the codes for the current page
  const currentCodes = sortedAccessCodes.slice(
    indexOfFirstCode,
    indexOfLastCode
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const fetchBusinessData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/business/getBusinessById/${id}`
      );
      // console.log(response.data.data, "responseeeee")
      setBusinessData(response.data.data);
      setIsDeactivated(response.data.data.status);
    } catch (error) {
      console.error("Error fetching active providers:", error);
    }
  };
  useEffect(() => {
    fetchBusinessData();
  }, []);

  if (!businessData) {
    return <div>No Business Data Available</div>;
  }

  const handleAccessCodeChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setAccessCode(value);
    }
  };

  const handleStatusChange = (action) => {
    setActionType(action);
    setShowModal(true);
    setIsLoading(true);
    setIsDeactivated((prev) => !prev);
  };

  const handleOpenAddModal = () => {
    setAddModalOpen(true);
  };

  const handleOpenEditModal = () => {
    setEditForm({
      businessName: businessData?.businessName || "",
      firstName: businessData?.firstName || "",
      lastName: businessData?.lastName || "",
      solutionsProAccess: Boolean(businessData?.solutionsProAccess),
      sessionsAvailable: businessData?.sessionsAvailable ?? "",
    });
    setIsEditing(true);
  };

  const handleCloseEditModal = () => {
    setIsEditing(false);
  };

  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => {
      if (name === "solutionsProAccess") {
        return {
          ...prev,
          solutionsProAccess: checked,
          sessionsAvailable: checked ? prev.sessionsAvailable : "",
        };
      }
      return {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
    });
  };

  const handleUpdateBusiness = async () => {
    if (!editForm.businessName.trim()) {
      toast.error("Business Name is required.");
      return;
    }
    if (!editForm.firstName.trim() || !editForm.lastName.trim()) {
      toast.error("First Name and Last Name are required.");
      return;
    }
    if (
      editForm.solutionsProAccess &&
      (!editForm.sessionsAvailable.toString().trim() ||
        isNaN(Number(editForm.sessionsAvailable)) ||
        Number(editForm.sessionsAvailable) < 1)
    ) {
      toast.error(
        "Sessions Available must be a positive number when Solutions Pro Access is enabled."
      );
      return;
    }

    setIsLoading(true);
    try {
      const request = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/business/updateBusiness/${id}`,
        {
          businessName: editForm.businessName.trim(),
          firstName: editForm.firstName.trim(),
          lastName: editForm.lastName.trim(),
          solutionsProAccess: editForm.solutionsProAccess,
          sessionsAvailable: editForm.solutionsProAccess
            ? Number(editForm.sessionsAvailable)
            : 0,
        }
      );

      if (request.data.success) {
        toast.success(request.data.message);
        setBusinessData(request.data.data);
        setIsEditing(false);
      } else {
        toast.error(request.data.message || "Could not update business.");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Network connection lost. Please check your connection and try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModalBusiness = () => {
    setShowModal(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setActionType(null);
    setModalOpen(false);
    setAddModalOpen(false);
    setIsEditing(false);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
  };

  const handleStatusChangeForAccessCode = async (status, accessCodeId) => {
    let actionType, endpoint, newStatus;
    
    if (status === "Active") {
      actionType = "deactivate";
      endpoint = `/access/deactivateAccessCode/${accessCodeId}`;
      newStatus = "Inactive";
    } else if (status === "Unused") {
      actionType = "activate";
      endpoint = `/access/activateAccessCode/${accessCodeId}`;
      newStatus = "Active";
    } else {
      // Inactive
      actionType = "activate";
      endpoint = `/access/activateAccessCode/${accessCodeId}`;
      newStatus = "Active";
    }

    try {
      const request = await axios.post(
        `${process.env.REACT_APP_BASE_URL}${endpoint}`
      );

      if (request.data.success) {
        toast.success(request.data.message);
        const newAccessCodes = businessData?.access_codes.map((accessCode) => {
          if (accessCode._id === accessCodeId) {
            return {
              ...accessCode,
              status: newStatus,
            };
          }
          return accessCode;
        });
        setBusinessData({ ...businessData, access_codes: newAccessCodes });
      } else {
        toast.error(`Could not ${actionType} access code.`);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        "Network connection lost. Please check your connection and try again."
      );
    }
  };

  const handleAddAccessCode = async () => {
    const accessCodeInt = parseInt(accessCode, 10);

    if (isNaN(accessCodeInt) || accessCodeInt < 1 || accessCodeInt > 100) {
      toast.error("Please enter a valid access code between 1 and 100.");
      return;
    }

    setIsLoading(true);

    try {
      const request = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/business/addadditionalaccesscode/${id}`,
        { accessCode: accessCodeInt }
      );

      if (request.data.success) {
        toast.success(request.data.message);
        setAccessCode("");
        setBusinessData(request.data.data.businessModels[0]);
        handleCloseAddModal();
      } else {
        toast.error(
          `Could not ${actionType === "deactivate" ? "deactivate" : "activate"
          } business.`
        );
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        "Network connection lost. Please check your connection and try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmAction = async (actionType) => {
    if (actionType === "deactivate") {
      handleStatusChange("deactivate");
    } else {
      handleStatusChange("activate");
    }
    setShowModal(false);
    setIsLoading(true);
    setModalOpen(true);

    try {
      const request = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/business/${actionType}Business/${id}`
      );
      if (request.data.success) {
        toast.success(request.data.message);
      } else {
        toast.error(
          `Could not ${actionType === "deactivate" ? "deactivate" : "activate"
          } business.`
        );
      }
      setIsLoading(false);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        "Network connection lost. Please check your connection and try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Business Profile */}
      <div style={styles.businessProfile}>
        <div style={styles.profileHeader} className="px-5 pe-0">
          <h2 style={styles.fontSize}>Business Profile</h2>
          <div style={styles.profileButtons}>
            <>
              <button
                style={{
                  ...styles.btn,
                  backgroundColor: isDeactivated ? "#FF0000" : "#00FF00",
                }}
                onClick={() => setShowModal(true)}
                disabled={isLoading}
              >
                {isDeactivated
                  ? isLoading
                    ? "Deactivate"
                    : "Deactivate"
                  : isLoading
                    ? "Activate"
                    : "Activate"}
              </button>
              <button
                style={{ ...styles.btn, ...styles.btnEdit }}
                onClick={handleOpenEditModal}
                disabled={isLoading}
              >
                Edit
              </button>
            </>
          </div>
        </div>
        <div style={styles.profileInfo} className="ps-5 pt-3">
          <div className="row">
            <div className="col-lg-6 col-md-6 col-12">
              <p className="mb-4">
                <strong>Business ID:</strong> {businessData.businessIdentifier}
              </p>
            </div>
            <div className="col-lg-6 col-md-6 col-12">
              <p className="mb-4">
                <strong>Business Name:</strong> {businessData.businessName}
              </p>
            </div>
            <div className="col-lg-6 col-md-6 col-12">
              <p className="mb-4">
                <strong>Total Access Codes:</strong>{" "}
                {businessData.access_codes.length}
              </p>
            </div>
            <div className="col-lg-6 col-md-6 col-12">
              <p className="mb-4">
                <strong>Active Access Codes:</strong>{" "}
                {
                  businessData.access_codes.filter(
                    (code) => code.status === "Active"
                  ).length
                }
              </p>
            </div>

            <div className="col-lg-6 col-md-6 col-12">
              <p className="mb-4">
                <strong>Solutions Pro Access:</strong>{" "}
                {businessData.solutionsProAccess ? "Yes" : "No"}
              </p>
            </div>
            <div className="col-lg-6 col-md-6 col-12">
              <p className="mb-4">
                <strong>Sessions Available:</strong>{" "}
                {
                  businessData.sessionsAvailable || 0
                }
              </p>
            </div>
            <div className="col-lg-6 col-md-6 col-12">
              <p className="mb-4">
                <strong>Business Super Admin:</strong>{" "}
                {`${businessData.firstName} ${businessData.lastName}`}
              </p>
            </div>
            <div className="col-lg-6 col-md-6 col-12">
              <p className="mb-4">
                <strong>Email:</strong>{" "}
                {businessData.businessEmail}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Access Codes Table */}
      <div
        className="overlay-table"
      // style={
      //   isDeactivated
      //     ? { ...styles.accessCodes}
      //     : { ...styles.accessCodes, backgroundColor: 'lightgray', borderRadius: '10px'}
      // }
      >
        <div
          className="overlay"
          style={
            isDeactivated
              ? { ...styles.accessCodes }
              : { ...styles.accessCodes, display: "block" }
          }
        ></div>
        <div style={styles.tableHeader} className="ps-5 align-items-center">
          <h2 style={styles.fontSize} className="mb-0">
            Access Codes
          </h2>
          <div style={{ width: "200px", textAlign: "center" }}>
            {role !== "teamMember" && (
              <button
                className="m-0"
                style={{ ...styles.btn, ...styles.btnEdit }}
                onClick={handleOpenAddModal}
                disabled={isLoading} // Disable add button during loading
              >
                Add
              </button>
            )}
          </div>
        </div>
        <div>
          <table style={styles.codesTable}>
            <thead style={styles.tableHead}>
              <tr>
                <th style={styles.tableHeadCell} className="text-center">
                  Access Code
                </th>
                <th style={styles.tableHeadCell} className="text-center">
                  Name
                </th>
                <th style={styles.tableHeadCell} className="text-center">
                  Activation Date
                </th>
                <th style={styles.tableHeadCell} className="text-center">
                  Creation Date
                </th>
                <th style={styles.tableHeadCell} className="text-center">
                  Sessions Used
                </th>
                <th style={styles.tableHeadCell} className="text-center">
                  Sessions Remaining
                </th>
                <th style={styles.tableHeadCell} className="text-center">
                  Status
                </th>
                <th style={styles.tableHeadCell} className="text-center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {currentCodes.map((code, index) => {
                const sessionsUsed = code.status === "Active" 
                  ? (code.sessionsUsed !== undefined && code.sessionsUsed !== null 
                      ? code.sessionsUsed 
                      : 0)
                  : null;
                
                const sessionsAvailable = businessData.sessionsAvailable || 0;
                const sessionsRemaining = code.status === "Active" 
                  ? Math.max(0, sessionsAvailable - (sessionsUsed || 0))
                  : null;

                return (
                  <tr key={index} style={styles.tableRow}>
                    <td className="text-center" style={styles.tableCell}>
                      {code.accesscode}
                    </td>
                    <td className="text-center" style={styles.tableCell}>
                      {code.name || "-"}
                    </td>
                    <td className="text-center" style={styles.tableCell}>
                      {code.UserJoinData ? formatDate(code.UserJoinData) : "-"}
                    </td>
                    <td className="text-center" style={styles.tableCell}>
                      {formatDate(code.createdAt) || "-"}
                    </td>
                    <td className="text-center" style={styles.tableCell}>
                      {sessionsUsed !== null ? sessionsUsed : "-"}
                    </td>
                    <td className="text-center" style={styles.tableCell}>
                      {sessionsRemaining !== null ? sessionsRemaining : "-"}
                    </td>
                    <td className="text-center" style={styles.tableCell}>
                      {code.status}
                    </td>
                  <td className="text-center" style={styles.tableCell}>
                    <Dropdown>
                      <Dropdown.Toggle
                        style={styles.dropdownToggle}
                        id={`dropdown-${code._id}`}
                      >
                        <BsThreeDots style={{ fontSize: '18px' }} />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {code.status === "Active" ? (
                          <Dropdown.Item
                            onClick={() =>
                              handleStatusChangeForAccessCode(
                                code.status,
                                code._id
                              )
                            }
                            disabled={isLoading}
                          >
                            Deactivate
                          </Dropdown.Item>
                        ) : code.status === "Unused" ? (
                          <Dropdown.Item
                            onClick={() =>
                              handleStatusChangeForAccessCode(
                                code.status,
                                code._id
                              )
                            }
                            disabled={isLoading}
                          >
                            Active
                          </Dropdown.Item>
                        ) : (
                          <Dropdown.Item
                            onClick={() =>
                              handleStatusChangeForAccessCode(
                                code.status,
                                code._id
                              )
                            }
                            disabled={isLoading}
                          >
                            Activate
                          </Dropdown.Item>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
                  );
                })}
            </tbody>
            {isAddModalOpen && (
              <div style={styles.modalOverlayAccess}>
                <div style={styles.modalContainerAccess}>
                  <h2 style={styles.headerAccess}>Add Access Codes</h2>
                  <input
                    type="text"
                    placeholder="Enter Access Codes"
                    style={styles.inputInlineStyle}
                    value={accessCode}
                    onChange={(e) => handleAccessCodeChange(e)}
                  />
                  <button
                    style={styles.confirmButton}
                    onClick={handleAddAccessCode}
                    disabled={isLoading}
                  >
                    Add Access Code
                  </button>
                  <button
                    style={styles.cancelButton}
                    onClick={handleCloseAddModal}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {isModalOpen && actionType === "deactivate" && (
              <div style={{ ...styles.modalOverlayDeactivate }}>
                <div style={{ ...styles.modalContainer, maxWidth: "44%" }}>
                  <p style={styles.modalText}>
                    Business Inactive <br />
                    <br /> Users linked to the business are inactive.
                  </p>
                  <button
                    style={styles.cancelButton}
                    onClick={handleCloseModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </table>
        </div>

        {/* Pagination */}
        {checkPermissionByKey(user, "accessCodes", "AccessCodesListAccess") && (
          <TablePaginationBar
            currentPage={currentPage}
            totalItems={accessCodes.length}
            rowsPerPage={codesPerPage}
            onPageChange={paginate}
          />
        )}
      </div>

      {/* Modal Component */}
      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContainer}>
            <p style={styles.modalText}>
              {!isDeactivated ? (
                <>
                  Activating this business will restore access for all users
                  linked to this business. Are you sure you want to proceed?
                </>
              ) : (
                <>
                  Deactivating this business will revoke access for all users
                  linked to this business. Are you sure you want to proceed?
                </>
              )}
            </p>
            <button
              style={styles.confirmButton}
              className="mx-5"
              onClick={() =>
                handleConfirmAction(isDeactivated ? "deactivate" : "activate")
              }
            >
              {isDeactivated ? "Yes, deactivate" : "Yes, activate"}
            </button>
            <button
              style={styles.cancelButton}
              className="mx-5"
              onClick={handleCloseModalBusiness}
            >
              {isDeactivated ? "No, keep active" : "No, keep inactive"}
            </button>
          </div>
        </div>
      )}

      {isEditing && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modalContainer, maxWidth: "520px", width: "90%" }}>
            <h2 style={styles.headerAccess}>Edit Business Profile</h2>

            <label style={styles.editLabel}>Business ID</label>
            <input
              type="text"
              style={{ ...styles.inputInlineStyle, opacity: 0.7 }}
              value={businessData.businessIdentifier}
              disabled
            />

            <label style={styles.editLabel}>Business Name</label>
            <input
              type="text"
              name="businessName"
              style={styles.inputInlineStyle}
              value={editForm.businessName}
              onChange={handleEditFormChange}
            />

            <label style={styles.editLabel}>First Name</label>
            <input
              type="text"
              name="firstName"
              style={styles.inputInlineStyle}
              value={editForm.firstName}
              onChange={handleEditFormChange}
            />

            <label style={styles.editLabel}>Last Name</label>
            <input
              type="text"
              name="lastName"
              style={styles.inputInlineStyle}
              value={editForm.lastName}
              onChange={handleEditFormChange}
            />

            <label style={styles.editLabel}>Email</label>
            <input
              type="email"
              style={{ ...styles.inputInlineStyle, opacity: 0.7 }}
              value={businessData.businessEmail}
              disabled
            />

            <div style={styles.editToggleRow}>
              <label style={{ ...styles.editLabel, marginBottom: 0 }}>
                Solutions Pro Access
              </label>
              <input
                type="checkbox"
                name="solutionsProAccess"
                checked={editForm.solutionsProAccess}
                onChange={handleEditFormChange}
              />
            </div>

            {editForm.solutionsProAccess && (
              <>
                <label style={styles.editLabel}>Sessions Available</label>
                <input
                  type="number"
                  name="sessionsAvailable"
                  min="1"
                  style={styles.inputInlineStyle}
                  value={editForm.sessionsAvailable}
                  onChange={handleEditFormChange}
                />
              </>
            )}

            <button
              style={styles.confirmButton}
              onClick={handleUpdateBusiness}
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Save"}
            </button>
            <button
              style={styles.cancelButton}
              onClick={handleCloseEditModal}
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

const styles = {
  container: {
    fontFamily: "Poppins",
    fontWeight: 500,
    color: "#575757",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: "20px",
  },
  businessProfile: {
    backgroundColor: "#ffff",
    borderRadius: "10px",
    marginBottom: "20px",
  },
  profileHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
    backgroundColor: "#064260",
    color: "white",
    borderRadius: "10px 10px 0 0",
    padding: "10px 30px",
  },
  profileButtons: {
    display: "flex",
    gap: "10px",
    // marginRight: "3%"
    // width: "300px",
    textAlign: "end",
    // paddingRight: "15px",
  },
  btn: {
    padding: "10px 10px 10px 10px",
    border: "none",
    borderRadius: "4px",
    color: "#fff",
    backgroundColor: "#27B99A",
    cursor: "pointer",
    marginRight: "3%",
    width: "115px",
  },
  btnDeactivate: {
    backgroundColor: "#25BEFF",
  },
  btnEdit: {
    backgroundColor: "#27B99A",
    padding: "10px 40px",
  },
  profileInfo: {
    marginTop: "10px",
    color: "black",
    opacity: "87%",
    fontSize: "17px",
  },
  accessCodes: {
    // backgroundColor: "white",
    borderRadius: "10px",
  },
  tableHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "2px",
    backgroundColor: "#064260",
    color: "white",
    borderRadius: "10px 10px 0 0",
    padding: "10px 15px 10px 30px",
  },
  codesTable: {
    width: "100%",
    borderCollapse: "collapse",
    borderRadius: "0 0 10px 10px",
    overflow: "hidden",
  },
  tableHead: {
    backgroundColor: "#064260",
    color: "#fff",
  },
  tableHeadCell: {
    padding: "10px 8px",
    fontSize: "14px",
    whiteSpace: "nowrap",
    fontWeight: 600,
  },
  tableRow: {
    // backgroundColor: "#fff",
  },
  tableCell: {
    padding: "15px",
  },
  btnActivate: {
    backgroundColor: "#27B99A",
    padding: "10px 30px",
  },
  btnDeactivateAction: {
    backgroundColor: "#27B99A",
  },
  dropdownToggle: {
    backgroundColor: "#27B99A",
    border: "none",
    borderRadius: "4px",
    padding: "6px 12px",
    color: "white",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: 600,
  },
  fontSize: {
    fontSize: "21px",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContainer: {
    backgroundColor: "#fff",
    padding: "35px 46px 55px 46px",
    borderRadius: "20px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    maxWidth: "36%",
  },
  modalText: {
    fontSize: "18px",
    fontWeight: "700",
    wordWrap: "break-word",
    lineHeight: "1.5",
    fontFamily: "Poppins",
    fontSize: "15px",
  },
  confirmButton: {
    color: "#064260",
    border: "none",
    padding: "10px 15px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "700",
  },
  cancelButton: {
    color: "#064260",
    border: "none",
    padding: "10px 15px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "700",
  },
  modalOverlayAccess: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContainerAccess: {
    backgroundColor: "#fff",
    padding: "35px 46px 55px 46px",
    borderRadius: "20px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    width: "30%",
  },
  inputInlineStyle: {
    flex: "1",
    padding: "8px 40px 8px 8px",
    borderRadius: "4px",
    backgroundColor: "#F4F4F6",
    position: "relative",
    border: "none",
  },
  headerAccess: {
    fontSize: "16px",
    color: "#064260",
    textAlign: "left",
  },
  editLabel: {
    fontSize: "13px",
    color: "#064260",
    textAlign: "left",
    fontWeight: 600,
    marginBottom: "-4px",
  },
  editToggleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "6px",
  },
  modalOverlayDeactivate: {
    position: "absolute",
    top: "60%",
    left: "35%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "50%",
  },
};

export default BusinessDetails;
